// resources/js/Pages/HallOfFame/HallOfFameTable.jsx
import React, { useState, useEffect } from 'react';
import { useScriptData } from './hooks/useScriptData';
import { useFilters } from './hooks/useFilters';
import ScriptRow from './components/ScriptRow';
import VideoModal from './components/VideoModal';
import FilterBar from './components/FilterBar';
import GenreSunburstModal from './components/GenreSunburstModal';
import StartMessageModal from './components/StartMessageModal';

// Feature flag: Enable/disable the group/ungroup toggle button
const ENABLE_GROUP_TOGGLE = false;

export default function HallOfFameTable({ scripts, auth, onFilteredCountChange }) {
    const isVaultMode = scripts.length > 0 && scripts[0].isVault;
    const [sortConfig, setSortConfig] = useState({
        column: null,
        direction: 'desc'
    });
    const [isGrouped, setIsGrouped] = useState(true);
    const [videoModalOpen, setVideoModalOpen] = useState(false);
    const [selectedScript, setSelectedScript] = useState(null);
    
    // Genre modal state
    const [genreModalOpen, setGenreModalOpen] = useState(false);
    const [genreModalData, setGenreModalData] = useState(null);
    
    // Message modal state
    const [messageModalOpen, setMessageModalOpen] = useState(false);
    const [messageRecipient, setMessageRecipient] = useState({ name: '', endpoint: '' });
    
    // Use filters
    const {
        genres,
        scriptTypes,
        selectedGenres,
        selectedScriptTypes,
        filteredScripts,
        setSelectedGenres,
        setSelectedScriptTypes,
        clearFilters
    } = useFilters(scripts);
    
    // Notify parent component when filtered count changes
    useEffect(() => {
        if (onFilteredCountChange) {
            onFilteredCountChange(filteredScripts.length);
        }
    }, [filteredScripts.length, onFilteredCountChange]);
    
    // Apply sorting to filtered scripts
    const sortedScripts = useScriptData(filteredScripts, sortConfig);
    
    const handleSort = (column) => {
        setSortConfig(prev => ({
            column,
            direction: prev.column === column && prev.direction === 'desc' ? 'asc' : 'desc'
        }));
    };
    
    const SortableHeader = ({ column, label }) => {
        const isActive = sortConfig.column === column;
        
        // CHANGED: We use a DIV, not a TH. 
        // This prevents the "double header" bug that makes widths unpredictable.
        return (
            <div 
                onClick={(e) => {
                    e.stopPropagation(); // Stop click from bubbling up
                    handleSort(column);
                }} 
                className="sortable-header-container"
                style={{ cursor: 'pointer', height: '100%', width: '100%' }}
            >
                <h6>
                    {label}
                    {isActive && (sortConfig.direction === 'desc' ? ' ↓' : ' ↑')}
                </h6>
            </div>
        );
    };
    
    // Handle contact writer click
    const handleContactWriter = (script) => {
        // Check if user is logged in
        const isLoggedIn = auth?.user ? true : false;
        
        if (!isLoggedIn) {
            // Redirect to login
            window.location.href = '/login';
            return;
        }
        
        // Check if user is trying to message themselves (compare by nickname, not ID)
        const currentUserNickname = auth?.user?.nickname || null;
        const scriptAuthorNickname = script.author?.nickname || null;
        
        // Debug logging - remove after testing
        console.log('[HallOfFameTable.handleContactWriter Debug]', {
            scriptTitle: script.title,
            currentUserNickname,
            scriptAuthorNickname,
            authObject: auth,
            scriptAuthor: script.author,
            willBlock: currentUserNickname && scriptAuthorNickname && 
                      currentUserNickname === scriptAuthorNickname
        });
        
        if (currentUserNickname && scriptAuthorNickname && 
            currentUserNickname === scriptAuthorNickname) {
            alert('You cannot message yourself.');
            return;
        }
        
        // Open message modal
        setMessageRecipient({
            name: script.author?.display_name || script.author?.nickname || 'Writer',
            endpoint: `/messages/start/movie/${script.id}`
        });
        setMessageModalOpen(true);
    };
    
    const tableClass = isGrouped ? 'custom-table grouped_table' : 'custom-table ungrouped_table';
    
    return (
        <>
            {/* Filter Bar */}
            <FilterBar 
                genres={genres}
                scriptTypes={scriptTypes}
                selectedGenres={selectedGenres}
                selectedScriptTypes={selectedScriptTypes}
                onGenreChange={setSelectedGenres}
                onScriptTypeChange={setSelectedScriptTypes}
                onClearFilters={clearFilters}
                filteredCount={filteredScripts.length}
                totalCount={scripts.length}
            />
            
            {/* Results Count */}
            {/* <div className="mb-3 text-white">
                <p className="mb-0">
                    Showing {sortedScripts.length} of {scripts.length} scripts
                </p>
            </div> */}
            
            <div className="table-responsive">
                <div className={tableClass}>
                    {/* Group/Ungroup Toggle - Controlled by ENABLE_GROUP_TOGGLE flag */}
                    {ENABLE_GROUP_TOGGLE && (
                        <div className="group_sort">
                            <button 
                                className={`group_btn ${isGrouped ? 'inactive' : 'active'}`}
                                onClick={() => setIsGrouped(!isGrouped)}
                            >
                                <span>{isGrouped ? 'Ungroup Column' : 'Group Column'}</span>
                                <img src="/images/icons/expand_icon.svg" width="40" height="40" alt="Toggle" />
                            </button>
                        </div>
                    )}
                <table className="table" id="table-custom">
                    {/* GROUP HEADER - Shows in grouped mode with icons */}
                    <thead className="group_title">
                        <tr>
                            {/* Icon columns WITH icons in group header */}
                            {!isVaultMode && (
                            <th className="top_title icon_col text-center" title="Award Winner">
                                <i className="fa fa-star text-warning"></i>
                            </th>
                            )}
                            <th className="top_title icon_col text-center" title="Video">
                                <i className="fa fa-play text-info"></i>
                            </th>
                            <th className="top_title icon_col text-center" title="Analysis">
                                <i className="fa fa-bar-chart text-success"></i>
                            </th>
                            <th className="top_title icon_col text-center" title="Read Script">
                                <i className="fa fa-book text-primary"></i>
                            </th>
                            
                            {/* Main Sortable Columns */}
                            <th className="top_title" onClick={() => handleSort('title')} style={{ cursor: 'pointer' }}>
                                Title
                                <span className="d-block sorting position-static mt-2">
                                    <span className="arrows ar_up"><img src="/images/icons/sorting_down.svg" alt="▲" width="6" height="14" /></span>
                                    <span className="arrows ar_down"><img src="/images/icons/sorting_up.svg" alt="▼" width="6" height="14" /></span>
                                </span>
                            </th>
                            <th className="top_title" onClick={() => handleSort('genre')} style={{ cursor: 'pointer' }}>
                                Genre
                                <span className="d-block sorting position-static mt-2">
                                    <span className="arrows ar_up"><img src="/images/icons/sorting_down.svg" alt="▲" width="6" height="14" /></span>
                                    <span className="arrows ar_down"><img src="/images/icons/sorting_up.svg" alt="▼" width="6" height="14" /></span>
                                </span>
                            </th>
                            <th className="top_title" onClick={() => handleSort('pcr')} style={{ cursor: 'pointer' }}>
                                PCR
                                <span className="d-block sorting position-static mt-2">
                                    <span className="arrows ar_up"><img src="/images/icons/sorting_down.svg" alt="▲" width="6" height="14" /></span>
                                    <span className="arrows ar_down"><img src="/images/icons/sorting_up.svg" alt="▼" width="6" height="14" /></span>
                                </span>
                            </th>
                            {/* Avg Score — Script Vault only (see ScriptRow) */}
                            {isVaultMode && (
                            <th className="top_title pcr_score_col" onClick={() => handleSort('pcr_score')} style={{ cursor: 'pointer' }}>
                                Avg Score
                                <span className="d-block sorting position-static mt-2">
                                    <span className="arrows ar_up"><img src="/images/icons/sorting_down.svg" alt="▲" width="6" height="14" /></span>
                                    <span className="arrows ar_down"><img src="/images/icons/sorting_up.svg" alt="▼" width="6" height="14" /></span>
                                </span>
                            </th>
                            )}
                            <th className="top_title" onClick={() => handleSort('overall_avg')} style={{ cursor: 'pointer' }}>
                                Overall Avg
                                <span className="d-block sorting position-static mt-2">
                                    <span className="arrows ar_up"><img src="/images/icons/sorting_down.svg" alt="▲" width="6" height="14" /></span>
                                    <span className="arrows ar_down"><img src="/images/icons/sorting_up.svg" alt="▼" width="6" height="14" /></span>
                                </span>
                            </th>
                            <th className="top_title group_border" colSpan="3" onClick={() => handleSort('structure_avg')} style={{ cursor: 'pointer' }}>
                                Structure and Plot
                                <span className="d-block sorting position-static mt-2">
                                    <span className="arrows ar_up"><img src="/images/icons/sorting_down.svg" alt="▲" width="6" height="14" /></span>
                                    <span className="arrows ar_down"><img src="/images/icons/sorting_up.svg" alt="▼" width="6" height="14" /></span>
                                </span>
                            </th>
                            <th className="top_title group_border" colSpan="3" onClick={() => handleSort('characters_avg')} style={{ cursor: 'pointer' }}>
                                Characters
                                <span className="d-block sorting position-static mt-2">
                                    <span className="arrows ar_up"><img src="/images/icons/sorting_down.svg" alt="▲" width="6" height="14" /></span>
                                    <span className="arrows ar_down"><img src="/images/icons/sorting_up.svg" alt="▼" width="6" height="14" /></span>
                                </span>
                            </th>
                            <th className="top_title group_border" colSpan="3" onClick={() => handleSort('conflict_avg')} style={{ cursor: 'pointer' }}>
                                Conflict and Stakes
                                <span className="d-block sorting position-static mt-2">
                                    <span className="arrows ar_up"><img src="/images/icons/sorting_down.svg" alt="▲" width="6" height="14" /></span>
                                    <span className="arrows ar_down"><img src="/images/icons/sorting_up.svg" alt="▼" width="6" height="14" /></span>
                                </span>
                            </th>
                            <th className="top_title group_border" colSpan="3" onClick={() => handleSort('emotional_avg')} style={{ cursor: 'pointer' }}>
                                Emotional and Thematic
                                <span className="d-block sorting position-static mt-2">
                                    <span className="arrows ar_up"><img src="/images/icons/sorting_down.svg" alt="▲" width="6" height="14" /></span>
                                    <span className="arrows ar_down"><img src="/images/icons/sorting_up.svg" alt="▼" width="6" height="14" /></span>
                                </span>
                            </th>
                            <th className="top_title group_border" colSpan="5" onClick={() => handleSort('dialogue_premise_avg')} style={{ cursor: 'pointer' }}>
                                Dialogue Premise
                                <span className="d-block sorting position-static mt-2">
                                    <span className="arrows ar_up"><img src="/images/icons/sorting_down.svg" alt="▲" width="6" height="14" /></span>
                                    <span className="arrows ar_down"><img src="/images/icons/sorting_up.svg" alt="▼" width="6" height="14" /></span>
                                </span>
                            </th>
                        </tr>
                    </thead>
                    
                    {/* DETAIL HEADER - Only shows in ungrouped mode */}
                    <thead className="detail_header">
                        <tr>
                            {/* Icon columns - horizontal */}
                            {!isVaultMode && (
                            <th className="no_sort text-center" title="Award Winner" style={{ width: '40px' }}>
                                <i className="fa fa-star text-warning"></i>
                            </th>
                            )}
                            <th className="no_sort text-center" title="Video" style={{ width: '40px' }}>
                                <i className="fa fa-play text-info"></i>
                            </th>
                            <th className="no_sort text-center" title="Analysis" style={{ width: '40px' }}>
                                <i className="fa fa-line-chart text-success"></i>
                            </th>
                            <th className="no_sort text-center" title="Read Script" style={{ width: '40px' }}>
                                <i className="fa fa-book text-primary"></i>
                            </th>
                            
                            {/* Title, Genre, PCR - HORIZONTAL, not rotated */}
                            <th className="text-start no_rotate"><SortableHeader column="title" label="Screenplay Title" /></th>
                            <th className="text-start no_rotate"><SortableHeader column="genre" label="Genre" /></th>
                            <th className="no_rotate text-center"><SortableHeader column="pcr" label="P/C/R" /></th>
                            {isVaultMode && (
                            <th className="no_rotate text-center pcr_score_col"><SortableHeader column="pcr_score" label="Avg Score" /></th>
                            )}

                            {/* OVERALL - Still horizontal */}
                            <th className="no_rotate overall_average text-center"><SortableHeader column="overall_avg" label="Overall Average" /></th>
                            
                            {/* NOW START ROTATED COLUMNS */}
                            <th className="script_color_th colspan_group"><SortableHeader column="sl_overall_grade" label="Overall (Script)" /></th>
                            <th className="colspan_group"><SortableHeader column="rating" label="Overall (Scene)" /></th>
                            <th className="script_color_th group_border colspan_group"><SortableHeader column="sl_structure_grade" label="Structure/Plot (Script)" /></th>
                            <th className="colspan_group"><SortableHeader column="plotrating" label="Plot (Scene)" /></th>
                            <th className="colspan_group"><SortableHeader column="story_forward" label="Move Story Forward" /></th>
                            <th className="script_color_th group_border colspan_group"><SortableHeader column="sl_characters_grade" label="Characters (Script)" /></th>
                            <th className="colspan_group"><SortableHeader column="charactersrating" label="Characters (Scene)" /></th>
                            <th className="colspan_group"><SortableHeader column="character_changes" label="Character Changes" /></th>
                            <th className="script_color_th group_border colspan_group"><SortableHeader column="sl_conflict_grade" label="Conflict (Script)" /></th>
                            <th className="colspan_group"><SortableHeader column="conflict_level" label="Conflict (Scene)" /></th>
                            <th className="colspan_group"><SortableHeader column="stakes" label="High Stakes" /></th>
                            <th className="script_color_th group_border colspan_group"><SortableHeader column="sl_ei_grade" label="Emotional Impact (Script)" /></th>
                            <th className="colspan_group"><SortableHeader column="emotional_impact" label="Emotional Impact (Scene)" /></th>
                            <th className="script_color_th colspan_group"><SortableHeader column="sl_theme_grade" label="Theme (Script)" /></th>
                            <th className="group_border colspan_group"><SortableHeader column="dialoguerating" label="Dialogue (Scene)" /></th>
                            <th className="script_color_th colspan_group"><SortableHeader column="sl_vi_grade" label="Visual Imagery (Script)" /></th>
                            <th className="script_color_th colspan_group"><SortableHeader column="sl_originality_grade" label="Originality (Script)" /></th>
                            <th className="colspan_group"><SortableHeader column="conceptrating" label="Concept (Scene)" /></th>
                            <th className="script_color_th colspan_group"><SortableHeader column="sl_premise_grade" label="Premise (Script)" /></th>
                        </tr>
                    </thead>
                    
                    <tbody>
                        {sortedScripts.length > 0 ? (
                            sortedScripts.map((script) => (
                                <ScriptRow 
                                    key={script._uniqueKey}
                                    script={script}
                                    allScripts={sortedScripts}
                                    onVideoClick={(script) => {
                                        setSelectedScript(script);
                                        setVideoModalOpen(true);
                                    }}
                                    onGenreClick={(data) => {
                                        setGenreModalData(data);
                                        setGenreModalOpen(true);
                                    }}
                                    onContactWriterClick={handleContactWriter}
                                    auth={auth}
                                />
                            ))
                        ) : (
                            <tr>
                                <td colSpan="27" className="text-center py-5">
                                    <h4 className="text-white">No scripts match your filters</h4>
                                    <button 
                                        className="btn btn-primary mt-3"
                                        onClick={clearFilters}
                                    >
                                        Clear Filters
                                    </button>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
            
            {/* Video Modal */}
            <VideoModal 
                isOpen={videoModalOpen}
                onClose={() => setVideoModalOpen(false)}
                initialScript={selectedScript}
                allScripts={sortedScripts}
            />
            
            {/* Genre Sunburst Modal */}
            <GenreSunburstModal 
                show={genreModalOpen}
                onHide={() => setGenreModalOpen(false)}
                data={genreModalData}
            />
            
            {/* Message Modal */}
            <StartMessageModal 
                show={messageModalOpen}
                onHide={() => setMessageModalOpen(false)}
                recipientName={messageRecipient.name}
                endpoint={messageRecipient.endpoint}
                isLoggedIn={auth?.user ? true : false}
            />
        </>
    );
}